import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Search, Loader2, ShieldCheck, ShieldOff, Ban, RotateCcw, Pencil, X, Eye, Download, ArrowRight } from "lucide-react";
import ImpersonationLogCard from "@/pages/app/admin/ImpersonationLogCard";
import { downloadCsv } from "@/lib/csv";

const TIER_OPTIONS = ["", "free", "pro", "hustler", "team"];
const TIER_BADGE = {
  free: "bg-slate-200 text-slate-700",
  pro: "bg-emerald-100 text-emerald-700",
  hustler: "bg-sky-100 text-sky-700",
  team: "bg-indigo-100 text-indigo-700",
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const { startImpersonation } = useAuth();
  const nav = useNavigate();

  const onImpersonate = async (u) => {
    if (!window.confirm(`Sign in as ${u.email}? You'll see their entire workspace until you click Exit.`)) return;
    try {
      await startImpersonation(u.id);
      toast.success(`Now viewing as ${u.email}`);
      nav("/v3", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impersonation failed");
    }
  };

  const onExport = async () => {
    try {
      const r = await api.get("/admin/users", { params: { q, limit: 5000 } });
      const all = r.data.users || [];
      const columns = [
        { label: "email", accessor: (r) => r.email },
        { label: "name", accessor: (r) => r.name || "" },
        { label: "role", accessor: (r) => r.role },
        { label: "tier", accessor: (r) => r.tier },
        { label: "tier_override", accessor: (r) => r.tier_override || "" },
        { label: "subscription_status", accessor: (r) => r.subscription_status },
        { label: "suspended", accessor: (r) => r.suspended ? "yes" : "no" },
        { label: "lead_count", accessor: (r) => r.lead_count },
        { label: "stripe_customer_id", accessor: (r) => r.stripe_customer_id || "" },
        { label: "brand_company", accessor: (r) => r.brand_company || "" },
        { label: "created_at", accessor: (r) => r.created_at || "" },
      ];
      const stamp = new Date().toISOString().slice(0, 10);
      downloadCsv(`ispeedtoclose-users-${stamp}.csv`, columns, all);
      toast.success(`Exported ${all.length} user${all.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    }
  };

  const refresh = useCallback(async (query = q) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users", { params: { q: query, limit: 100 } });
      setUsers(r.data.users || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refresh("");
  }, [refresh]);

  const onSearch = (e) => {
    e.preventDefault();
    refresh(q);
  };

  const patch = async (id, body, successMsg) => {
    try {
      await api.patch(`/admin/users/${id}`, body);
      toast.success(successMsg);
      refresh(q);
      setEditing(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div data-testid="admin-users">
      <ImpersonationLogCard />
      <div className="card p-4 mb-4">
        <form onSubmit={onSearch} className="flex gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[260px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
            <input
              data-testid="admin-users-search"
              className="input pl-9"
              placeholder="Search by email or name…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-primary" data-testid="admin-users-search-btn">
            Search
          </button>
          {q && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => { setQ(""); refresh(""); }}
              data-testid="admin-users-clear"
            >
              Clear
            </button>
          )}
          <div className="text-sm text-[var(--ink-2)] flex items-center ml-auto">
            <strong className="text-[var(--ink)]">{total}</strong>&nbsp;total
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onExport}
            data-testid="admin-users-export-csv"
          >
            <Download size={14} /> Export CSV
          </button>
        </form>
      </div>

      {loading ? (
        <div className="card p-8 text-center text-[var(--ink-2)]">
          <Loader2 size={20} className="animate-spin inline" /> Loading…
        </div>
      ) : users.length === 0 ? (
        <div className="card p-8 text-center text-[var(--ink-2)]">No users match.</div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-3 py-2 font-semibold">User</th>
                <th className="px-3 py-2 font-semibold">Tier</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Role</th>
                <th className="px-3 py-2 font-semibold text-right">Leads</th>
                <th className="px-3 py-2 font-semibold">Joined</th>
                <th className="px-3 py-2 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  className={"border-t " + (u.suspended ? "bg-rose-50/40" : "")}
                  data-testid={`admin-user-row-${u.id}`}
                >
                  <td className="px-3 py-2">
                    <Link
                      to={`/app/admin/users/${u.id}`}
                      className="block hover:text-[var(--primary)] transition-colors"
                      data-testid={`admin-user-deep-link-${u.id}`}
                    >
                      <div className="font-semibold flex items-center gap-1">{u.name || "—"} <ArrowRight size={11} className="opacity-50" /></div>
                      <div className="text-xs text-[var(--ink-2)] truncate max-w-[260px]">{u.email}</div>
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    <span className={"inline-block px-2 py-0.5 rounded text-xs font-semibold " + (TIER_BADGE[u.tier] || "bg-slate-200")}>
                      {u.tier}
                    </span>
                    {u.tier_override && (
                      <span className="ml-1 text-[10px] text-amber-700 uppercase tracking-wide">override</span>
                    )}
                    {u.is_comp && (
                      <span
                        className="ml-1 inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wide"
                        title={u.comp_reason || "Founder comp"}
                        data-testid={`admin-user-comp-badge-${u.id}`}
                      >
                        Comp
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-[var(--ink-2)]">{u.subscription_status}</td>
                  <td className="px-3 py-2">
                    {u.role === "admin" ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-violet-700">
                        <ShieldCheck size={12} /> admin
                      </span>
                    ) : (
                      <span className="text-xs text-[var(--ink-2)]">user</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{u.lead_count}</td>
                  <td className="px-3 py-2 text-xs text-[var(--ink-2)]">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        title="Edit"
                        onClick={() => setEditing(u)}
                        data-testid={`admin-user-edit-${u.id}`}
                      >
                        <Pencil size={12} />
                      </button>
                      {u.role !== "admin" && !u.suspended && (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm text-sky-700"
                          title="Sign in as this user"
                          onClick={() => onImpersonate(u)}
                          data-testid={`admin-user-impersonate-${u.id}`}
                        >
                          <Eye size={12} />
                        </button>
                      )}
                      {u.suspended ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm text-emerald-700"
                          onClick={() => patch(u.id, { suspended: false }, "User reinstated")}
                          title="Reinstate"
                          data-testid={`admin-user-reinstate-${u.id}`}
                        >
                          <RotateCcw size={12} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm text-rose-700"
                          onClick={() => {
                            if (window.confirm(`Suspend ${u.email}?`))
                              patch(u.id, { suspended: true }, "User suspended");
                          }}
                          title="Suspend"
                          data-testid={`admin-user-suspend-${u.id}`}
                        >
                          <Ban size={12} />
                        </button>
                      )}
                      {u.role === "admin" ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          title="Demote to user"
                          onClick={() => patch(u.id, { role: "user" }, "Role updated")}
                          data-testid={`admin-user-demote-${u.id}`}
                        >
                          <ShieldOff size={12} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm text-violet-700"
                          title="Promote to admin"
                          onClick={() => {
                            if (window.confirm(`Promote ${u.email} to admin?`))
                              patch(u.id, { role: "admin" }, "Promoted to admin");
                          }}
                          data-testid={`admin-user-promote-${u.id}`}
                        >
                          <ShieldCheck size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSave={(body) => patch(editing.id, body, "User updated")}
        />
      )}
    </div>
  );
}

function EditUserModal({ user, onClose, onSave }) {
  const [role, setRole] = useState(user.role || "user");
  const [tierOverride, setTierOverride] = useState(user.tier_override || "");
  const [suspended, setSuspended] = useState(!!user.suspended);
  const [isComp, setIsComp] = useState(!!user.is_comp);
  const [compReason, setCompReason] = useState(user.comp_reason || "");

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="admin-edit-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs text-[var(--ink-2)]">Editing</div>
            <div className="font-display font-extrabold text-xl">{user.email}</div>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            data-testid="admin-edit-close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-semibold text-[var(--ink)]">Role</span>
            <select
              className="input"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="admin-edit-role"
            >
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-semibold text-[var(--ink)]">Tier override</span>
            <select
              className="input"
              value={tierOverride}
              onChange={(e) => setTierOverride(e.target.value)}
              data-testid="admin-edit-tier"
            >
              {TIER_OPTIONS.map((t) => (
                <option key={t || "none"} value={t}>{t || "— no override —"}</option>
              ))}
            </select>
            <span className="text-xs text-[var(--ink-3)]">
              Overrides the Stripe-resolved tier. Use for comped accounts or testing.
            </span>
          </label>

          {/* Founder Comp — permanent free Team tier, no Stripe billing */}
          <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={isComp}
                onChange={(e) => setIsComp(e.target.checked)}
                data-testid="admin-edit-comp"
              />
              <span className="font-semibold text-amber-900">Founder Comp — Free Team tier</span>
            </label>
            <p className="mt-1 ml-6 text-xs text-amber-800">
              Grants full Team-tier entitlements permanently. Bypasses Stripe billing entirely —
              no charges, no seat overage, no MRR pollution. Use for founder / staff / family accounts.
            </p>
            {isComp && (
              <input
                type="text"
                className="input mt-2 text-sm"
                placeholder="Reason (e.g. Founder · Son (VA) · Beta partner)"
                value={compReason}
                onChange={(e) => setCompReason(e.target.value)}
                data-testid="admin-edit-comp-reason"
              />
            )}
            {user.comp_granted_by && (
              <div className="mt-2 ml-6 text-[11px] text-amber-700">
                Granted by <b>{user.comp_granted_by}</b>
                {user.comp_granted_at && ` · ${new Date(user.comp_granted_at).toLocaleDateString()}`}
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={suspended}
              onChange={(e) => setSuspended(e.target.checked)}
              data-testid="admin-edit-suspended"
            />
            <span>Suspended (blocks login)</span>
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="admin-edit-save"
            onClick={() => onSave({
              role,
              tier_override: tierOverride,
              suspended,
              is_comp: isComp,
              comp_reason: compReason,
            })}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
